/* One offer per session after dismissal; navigation never consumes an unseen offer. The home campaign panel already
   says it, so the home page never opens the offer by itself. Phones get a non-blocking sheet, and only on pages that do
   not already carry the offer in their own content. The theme editor behaves like the storefront, and opens the offer
   whenever its section is selected. */
(() => {
  'use strict';
  const instances = new Map();
  function init(root) {
    if (instances.has(root)) return;
    const key = `${root.dataset.storageKey}-seen`;
    const cleanups = [];
    const phone = matchMedia('(max-width: 749px)');
    const dialog = root.querySelector('[role="dialog"]');
    let timer, hideTimer, frame, lastFocused, seenOnPage = false, inertElements = [], sheet = false, waited = false;
    function listen(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      cleanups.push(() => target.removeEventListener(type, handler, options));
    }
    // Pages that already show the offer: the home campaign panel keeps the automatic popup quiet at every width; the product page note and the cart row quiet it on phones only.
    const OWN_OFFER = '#MainContent .xe-summer-feature, #MainContent .xe-product-summer-note, #MainContent .xe-campaign-row';
    const quiet = () => root.dataset.phoneScope !== 'all' && Boolean(document.querySelector('#MainContent .xe-summer-feature') || (phone.matches && document.querySelector(OWN_OFFER)));
    const seen = () => {
      if (seenOnPage) return true;
      try { if (sessionStorage.getItem(key) === '1') return true; } catch (_) { /* Cookie fallback. */ }
      try { return document.cookie.split('; ').includes(`${key}=1`); } catch (_) { return false; }
    };
    const remember = () => {
      seenOnPage = true;
      try { sessionStorage.setItem(key, '1'); } catch (_) { /* Cookie fallback. */ }
      try { document.cookie = `${key}=1; Path=/; SameSite=Lax`; } catch (_) { /* Page memory fallback. */ }
    };
    function releaseBackground() {
      inertElements.forEach(([element, wasInert]) => element.inert = wasInert);
      inertElements = [];
      document.documentElement.classList.remove('xe-popup-open');
    }
    function open(automatic = false) {
      clearTimeout(timer); clearTimeout(hideTimer);
      if (!root.isConnected || root.classList.contains('is-open')) return;
      if (automatic) {
        if (seen() || quiet()) return;
        if (document.hidden || document.querySelector('cart-drawer.active, quick-add-modal[open], product-modal[open], .menu-opening, #Details-HeaderCategories[open], details[open] .search-modal')) {
          timer = setTimeout(() => open(true), 1500); return;
        }
      }
      // Phones get a sheet that sits at the bottom edge: the page stays usable and nothing is taken from the shopper.
      sheet = phone.matches;
      if (sheet && automatic && !waited && window.scrollY < 240) {
        // Wait once for the first real scroll (or a pause) so the offer never covers the opening screen.
        waited = true;
        const proceed = () => { window.removeEventListener('scroll', onScroll); clearTimeout(timer); open(true); };
        const onScroll = () => { if (window.scrollY >= 240) proceed(); };
        window.addEventListener('scroll', onScroll, { passive: true });
        cleanups.push(() => window.removeEventListener('scroll', onScroll));
        timer = setTimeout(proceed, 12000);
        return;
      }
      lastFocused = document.activeElement;
      root.classList.toggle('is-sheet', sheet);
      dialog?.setAttribute('aria-modal', String(!sheet));
      root.hidden = false; root.setAttribute('aria-hidden', 'false');
      if (sheet) {
        document.documentElement.classList.add('xe-sheet-open');
        frame = requestAnimationFrame(() => root.classList.add('is-open'));
        return;
      }
      document.documentElement.classList.add('xe-popup-open');
      inertElements = [...document.querySelectorAll('#MainContent, .shopify-section-group-header-group, .shopify-section-group-footer-group, .xe-whatsapp-contact')]
        .filter(element => !element.contains(root)).map(element => [element, Boolean(element.inert)]);
      inertElements.forEach(([element]) => element.inert = true);
      frame = requestAnimationFrame(() => {
        root.classList.add('is-open');
        root.querySelector('[data-xe-summer-claim]')?.focus({ preventScroll: true });
      });
    }
    function close(shouldRemember = true) {
      clearTimeout(timer); clearTimeout(hideTimer); cancelAnimationFrame(frame);
      root.classList.remove('is-open');
      releaseBackground();
      document.documentElement.classList.remove('xe-sheet-open');
      // The sheet never moved focus, so only hand it back if the shopper was inside the sheet.
      if (!sheet || root.contains(document.activeElement)) {
        const focusTarget = lastFocused?.isConnected && lastFocused !== document.body && typeof lastFocused.focus === 'function' ? lastFocused : document.getElementById('MainContent');
        focusTarget?.focus({ preventScroll: true });
      }
      root.setAttribute('aria-hidden', 'true');
      if (shouldRemember) remember();
      hideTimer = setTimeout(() => root.hidden = true, matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 220);
    }
    root.querySelectorAll('[data-xe-summer-close]').forEach(button => listen(button, 'click', () => close()));
    // Rotating a phone can cross the breakpoint: a sheet must not turn into a dialog without its focus handling.
    const onMode = () => { if (root.classList.contains('is-open') && sheet !== phone.matches) close(false); };
    if (phone.addEventListener) { phone.addEventListener('change', onMode); cleanups.push(() => phone.removeEventListener('change', onMode)); }
    else if (phone.addListener) { phone.addListener(onMode); cleanups.push(() => phone.removeListener(onMode)); }
    listen(root.querySelector('[data-xe-summer-claim]'), 'click', () => remember());
    listen(document, 'keydown', event => {
      if (!root.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        if (sheet && !root.contains(document.activeElement)) return;
        event.preventDefault(); event.stopPropagation(); close(); return;
      }
      if (event.key !== 'Tab' || sheet) return;
      const elements = [...root.querySelectorAll('a[href], button:not([tabindex="-1"])')].filter(element => element.getClientRects().length);
      const first = elements[0], last = elements.at(-1);
      if (!first) return;
      if (!root.contains(document.activeElement) || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
        event.preventDefault(); (event.shiftKey ? last : first).focus();
      }
    }, true);
    listen(document, 'shopify:section:select', event => { if (event.detail.sectionId === root.dataset.sectionId) open(); });
    listen(document, 'shopify:section:deselect', event => { if (event.detail.sectionId === root.dataset.sectionId) close(false); });
    instances.set(root, () => {
      clearTimeout(timer); clearTimeout(hideTimer); cancelAnimationFrame(frame);
      if (inertElements.length) releaseBackground();
      cleanups.forEach(cleanup => cleanup());
    });
    if (!seen()) timer = setTimeout(() => open(true), Math.max(0, Number(root.dataset.delay) || 0));
  }
  const setup = (scope = document) => scope.querySelectorAll('[data-xe-summer-popup]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setup(), { once: true }); else setup();
  document.addEventListener('shopify:section:load', event => setup(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, destroy] of instances) if (event.target.contains(root)) { destroy(); instances.delete(root); }
  });
})();
